// ============================================
// Reports Page - Analytics and Reports
// ============================================

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart3,
  TrendingUp,
  Calendar,
  Download,
  Filter,
  Users,
  Target,
  DollarSign,
  Clock,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { dashboardApi } from '../lib/api';
import { PageLoader, ErrorState } from '../components';
import { cn, formatCurrency, formatNumber } from '../lib/utils';
import { useTranslation } from '../i18n/useTranslation';

type DateRange = '7d' | '30d' | '90d' | 'year';

export function Reports() {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<DateRange>('30d');

  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['reports', dateRange],
    queryFn: () => dashboardApi.stats(),
  });

  if (isLoading) return <PageLoader />;
  if (error) return <ErrorState message={t('errors.loadFailed')} />;

  const metrics = [
    {
      label: t('reports.totalLeads'),
      value: formatNumber(stats?.today?.newLeads || 0),
      change: '+12%',
      trend: 'up' as const,
      icon: Users,
      color: 'blue'
    },
    {
      label: t('reports.conversionRate'),
      value: `${Math.round((stats?.conversionRates?.eligibility_approval_rate || 0) * 100)}%`,
      change: '+5%',
      trend: 'up' as const,
      icon: Target,
      color: 'green'
    },
    {
      label: t('reports.avgResponseTime'),
      value: '2.4h',
      change: '-18%',
      trend: 'up' as const,
      icon: Clock,
      color: 'amber'
    },
    {
      label: t('reports.revenue'),
      value: formatCurrency(125000),
      change: '+23%',
      trend: 'up' as const,
      icon: DollarSign,
      color: 'emerald'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('reports.title')}</h1>
          <p className="text-slate-400 mt-1">{t('reports.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-800 rounded-lg p-1">
            {(['7d', '30d', '90d', 'year'] as DateRange[]).map((range) => (
              <button
                key={range}
                onClick={() => setDateRange(range)}
                className={cn('px-3 py-1.5 rounded-md text-sm font-medium transition-colors', dateRange === range ? 'bg-primary-500 text-white' : 'text-slate-400 hover:text-white')}
              >
                {t(`reports.${range}`)}
              </button>
            ))}
          </div>
          <button className="btn-secondary">
            <Download className="w-4 h-4" />
            {t('reports.export')}
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, i) => (
          <MetricCard key={i} {...metric} />
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Leads Over Time */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-white">{t('reports.leadsOverTime')}</h3>
            <BarChart3 className="w-5 h-5 text-slate-400" />
          </div>
          <div className="h-64 flex items-center justify-center text-slate-500">
            {t('reports.chartPlaceholder')}
          </div>
        </div>

        {/* Conversion Funnel */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-white">{t('reports.conversionFunnel')}</h3>
            <TrendingUp className="w-5 h-5 text-slate-400" />
          </div>
          <div className="space-y-3">
            <FunnelBar label={t('stages.NEW')} value={100} color="blue" />
            <FunnelBar label={t('stages.CONTACTED')} value={78} color="cyan" />
            <FunnelBar label={t('stages.WAITING_ELIGIBILITY')} value={52} color="amber" />
            <FunnelBar label={t('stages.APPROVED')} value={35} color="green" />
            <FunnelBar label={t('stages.PURCHASED')} value={18} color="emerald" />
          </div>
        </div>
      </div>

      {/* Top Performers */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white">{t('reports.topPerformers')}</h3>
          <button className="text-sm text-primary-400 hover:text-primary-300">{t('common.viewAll')}</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-right py-3 text-sm text-slate-400 font-medium">{t('reports.agent')}</th>
                <th className="text-right py-3 text-sm text-slate-400 font-medium">{t('reports.leadsHandled')}</th>
                <th className="text-right py-3 text-sm text-slate-400 font-medium">{t('reports.conversionRate')}</th>
                <th className="text-right py-3 text-sm text-slate-400 font-medium">{t('reports.avgResponseTime')}</th>
              </tr>
            </thead>
            <tbody>
              {[
                { name: 'Ahmed', leads: 145, rate: 42, time: '1.2h' },
                { name: 'Mohammed', leads: 132, rate: 38, time: '1.8h' },
                { name: 'Sara', leads: 128, rate: 45, time: '0.9h' },
              ].map((agent, i) => (
                <tr key={i} className="border-b border-slate-800/50">
                  <td className="py-3 text-white font-medium">{agent.name}</td>
                  <td className="py-3 text-slate-300">{agent.leads}</td>
                  <td className="py-3 text-green-400">{agent.rate}%</td>
                  <td className="py-3 text-slate-300">{agent.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

interface MetricCardProps { label: string; value: string; change: string; trend: 'up' | 'down'; icon: React.ElementType; color: string; }

function MetricCard({ label, value, change, trend, icon: Icon, color }: MetricCardProps) {
  const colorClasses: Record<string, string> = { blue: 'text-blue-400 bg-blue-500/10', green: 'text-green-400 bg-green-500/10', amber: 'text-amber-400 bg-amber-500/10', emerald: 'text-emerald-400 bg-emerald-500/10' };
  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div className={cn('p-2 rounded-lg', colorClasses[color])}><Icon className="w-5 h-5" /></div>
        <div className={cn('flex items-center gap-1 text-sm', trend === 'up' ? 'text-green-400' : 'text-red-400')}>
          {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
          {change}
        </div>
      </div>
      <div className="mt-3">
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-sm text-slate-400 mt-1">{label}</p>
      </div>
    </div>
  );
}

interface FunnelBarProps { label: string; value: number; color: string; }

function FunnelBar({ label, value, color }: FunnelBarProps) {
  const colorClasses: Record<string, string> = { blue: 'bg-blue-500', cyan: 'bg-cyan-500', amber: 'bg-amber-500', green: 'bg-green-500', emerald: 'bg-emerald-500' };
  return (
    <div>
      <div className="flex justify-between text-sm mb-1"><span className="text-slate-300">{label}</span><span className="text-slate-400">{value}%</span></div>
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden"><div className={cn('h-full rounded-full transition-all', colorClasses[color])} style={{ width: `${value}%` }} /></div>
    </div>
  );
}
